import {
  Badge as BadgeIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  InputAdornment,
  TextField,
  Button,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { prescriptionApi, practiceConnectApi } from "apis";
import CustomTable, { CustomTableColumn } from "components/common/CustomTable";
import DashboardLayout from "components/layouts/DashboardLayout";
import { EPath } from "constants/path";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { startOfDay, endOfDay } from "date-fns";
import {
  PrescriptionResponseDto,
  PrescriptionResponseDtoStatusEnum,
  GetPrescriptionsForProviderSortByEnum,
} from "libs/cellera-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useCelleraTheme } from "theme/useCelleraTheme";

import { CancelPrescriptionModal } from "./components/CancelPrescriptionModal";
import { ConnectWalletDialog } from "./components/ConnectWalletDialog";
import PrescriptionFilter, {
  PrescriptionFilterValues,
} from "./components/Prescription/PrescriptionFilter";
import { StatusTag } from "./components/Prescription/StatusTag";

export const PrescriptionManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const { colors } = useCelleraTheme();
  const { userClaims } = useAuth();
  const [searchValue, setSearchValue] = useState("");
  const [filters, setFilters] = useState<PrescriptionFilterValues>({
    statuses: [],
    fromDate: null,
    toDate: null,
    fromPrice: "",
    toPrice: "",
    providerIds: [],
  });
  const [request, setRequest] = useState({
    page: 1,
    pageSize: 10,
    search: "",
    statuses: undefined as string | undefined,
    fromDate: undefined as Date | undefined,
    toDate: undefined as Date | undefined,
    fromPrice: undefined as string | undefined,
    toPrice: undefined as string | undefined,
    providerIds: undefined as string | undefined,
    sortBy: undefined as GetPrescriptionsForProviderSortByEnum | undefined,
    orderBy: undefined as string | undefined,
  });

  // Fetch filter options
  const { data: filterOptions } = useQuery({
    queryKey: ["prescriptionFilters"],
    queryFn: async () => {
      const response = await prescriptionApi.providerGetPrescriptionFilters();
      return response;
    },
    retry: 2,
  });

  // Fetch Stripe Connect account status
  const { data: accountStatus } = useQuery({
    queryKey: ["account-status"],
    queryFn: async () => {
      const response = await practiceConnectApi.getStatus();
      return response;
    },
  });

  // Debounce search input with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setRequest((prev) => ({ ...prev, search: searchValue, page: 1 }));
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const {
    data: prescriptionsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["prescriptions", request],
    queryFn: async () => {
      const response = await prescriptionApi.getPrescriptionsForProvider({
        page: request.page,
        search: request.search,
        pageSize: request.pageSize,
        statuses: request.statuses,
        fromDate: request.fromDate,
        toDate: request.toDate,
        fromPrice: request.fromPrice,
        toPrice: request.toPrice,
        providerIds: request.providerIds,
        sortBy: request.sortBy,
        orderBy: request.orderBy?.toUpperCase(),
      });
      return {
        items: response.items,
        totalCount: response.total,
      };
    },
    retry: 2,
  });

  const totalCount = prescriptionsData?.totalCount || 0;
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPrescription, setSelectedPrescription] =
    useState<PrescriptionResponseDto | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isConnectWalletDialogOpen, setIsConnectWalletDialogOpen] =
    useState(false);

  const formatPrice = useCallback((price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  }, []);

  const formatPatientName = useCallback(
    (prescription: PrescriptionResponseDto) => {
      const text = [prescription.patientFirstName, prescription.patientLastName]
        .filter(Boolean)
        .join(" ");
      if (!text) return "N/A";
      return text;
    },
    []
  );

  const formatProviderName = useCallback(
    (prescription: PrescriptionResponseDto) => {
      return `Dr. ${prescription.providerFirstName} ${prescription.providerLastName}`;
    },
    []
  );

  const columns: CustomTableColumn<PrescriptionResponseDto>[] = useMemo(
    () => [
      {
        id: "id",
        label: "ID",
        getValue: (row) => row.id,
        sortable: false,
        sortAccessor: (row) => row.id,
      },
      {
        id: "patient",
        label: "Patient",
        getValue: (row) => (
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: colors.text.primary }}
            >
              {formatPatientName(row)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.text.secondary, fontSize: "0.75rem" }}
            >
              {row.patientEmail}
            </Typography>
          </Box>
        ),
        sortable: true,
        sortAccessor: (row) => `${row.patientFirstName} ${row.patientLastName}`,
      },
      {
        id: "datetime",
        label: "Datetime",
        getValue: (row) => {
          const date = new Date(row.createdAt);
          return date.toLocaleString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
        sortable: true,
        sortAccessor: (row) => row.createdAt,
      },
      {
        id: "price",
        label: "Price",
        getValue: (row) => formatPrice(row.price),
        sortable: false,
      },
      {
        id: "prescriber",
        label: "Prescriber",
        getValue: (row) => (
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: colors.text.primary }}
            >
              {formatProviderName(row)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.text.secondary, fontSize: "0.75rem" }}
            >
              {row.providerEmail}
            </Typography>
          </Box>
        ),
        sortable: false,
      },
      {
        id: "status",
        label: "Status",
        getValue: (row) => <StatusTag status={row.status} />,
        sortable: true,
        sortAccessor: (row) => row.status,
      },
      {
        id: "actions",
        label: "",
        getValue: (row) => (
          <IconButton
            size="small"
            onClick={(event) => handleMenuClick(event, row)}
            sx={{
              color: colors.text.secondary,
              "&:hover": {
                backgroundColor: colors.background.secondary,
                color: colors.text.primary,
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [colors]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value);
    },
    []
  );

  // Handle query errors
  useEffect(() => {
    if (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch data";
      showError(errorMessage);
    }
  }, [error, showError]);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRequest((prev) => ({
        ...prev,
        pageSize: parseInt(event.target.value, 10),
        page: 1,
      }));
    },
    []
  );

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setRequest((prev) => ({ ...prev, page: newPage + 1 }));
  }, []);

  const handleMenuClick = useCallback(
    (
      event: React.MouseEvent<HTMLElement>,
      prescription: PrescriptionResponseDto
    ) => {
      setSelectedPrescription(prescription);
      setMenuAnchorEl(event.currentTarget);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedPrescription(null);
  }, []);

  const handleFilterApply = useCallback(
    (newFilters: PrescriptionFilterValues) => {
      setFilters(newFilters);
      setRequest((prev) => ({
        ...prev,
        statuses:
          newFilters.statuses.length > 0
            ? newFilters.statuses.join(",")
            : undefined,
        fromDate: newFilters.fromDate
          ? startOfDay(newFilters.fromDate)
          : undefined,
        toDate: newFilters.toDate ? endOfDay(newFilters.toDate) : undefined,
        fromPrice: newFilters.fromPrice || undefined,
        toPrice: newFilters.toPrice || undefined,
        providerIds:
          newFilters.providerIds.length > 0
            ? newFilters.providerIds.join(",")
            : undefined,
        page: 1,
      }));
    },
    []
  );

  // Map column IDs to API sortBy enum values
  const getSortByValue = (
    columnId: string
  ): GetPrescriptionsForProviderSortByEnum | undefined => {
    const columnToSortByMap: Record<
      string,
      GetPrescriptionsForProviderSortByEnum
    > = {
      id: GetPrescriptionsForProviderSortByEnum.Id,
      patient: GetPrescriptionsForProviderSortByEnum.Patient,
      datetime: GetPrescriptionsForProviderSortByEnum.DateTime,
      status: GetPrescriptionsForProviderSortByEnum.Status,
    };
    return columnToSortByMap[columnId];
  };

  const handleSortChange = useCallback(
    (columnId: string, direction: "asc" | "desc") => {
      const sortByValue = getSortByValue(columnId);
      if (sortByValue) {
        setRequest((prev) => ({
          ...prev,
          sortBy: sortByValue,
          orderBy: direction,
          page: 1, // Reset to first page when sorting changes
        }));
      }
    },
    []
  );

  const handleCancelPrescription = useCallback(() => {
    setIsCancelModalOpen(true);
    setMenuAnchorEl(null);
  }, []);

  const handleConfirmCancelPrescription = useCallback(async () => {
    if (!selectedPrescription) return;
    try {
      setIsCancelling(true);
      await prescriptionApi.providerCancelPrescription({
        prescriptionId: selectedPrescription.id,
      });
      showSuccess("Prescription cancelled successfully");
      setIsCancelModalOpen(false);
      setSelectedPrescription(null);
      refetch();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to cancel prescription";
      showError(errorMessage);
    } finally {
      setIsCancelling(false);
    }
  }, [selectedPrescription, showSuccess, showError, refetch]);

  const getColumnIdFromSortBy = useCallback(
    (
      sortBy: GetPrescriptionsForProviderSortByEnum | undefined
    ): string | null => {
      if (!sortBy) return null;

      const enumKey = Object.keys(GetPrescriptionsForProviderSortByEnum).find(
        (key) =>
          GetPrescriptionsForProviderSortByEnum[
            key as keyof typeof GetPrescriptionsForProviderSortByEnum
          ] === sortBy
      );

      // Map enum keys to column IDs
      const enumToColumnMap: Record<string, string> = {
        Id: "id",
        Patient: "patient",
        DateTime: "datetime",
        Status: "status",
      };

      return enumKey ? enumToColumnMap[enumKey] || null : null;
    },
    []
  );

  const handleNewPrescriptionClick = useCallback(() => {
    // Check if Stripe is connected
    if (
      !accountStatus?.hasAccount ||
      !accountStatus?.canReceivePayouts ||
      !accountStatus?.chargesEnabled ||
      !accountStatus?.isOnboarded
    ) {
      setIsConnectWalletDialogOpen(true);
      return;
    }
    // If Stripe is connected, proceed to create prescription
    navigate(EPath.PRESCRIPTION_CREATE);
  }, [
    accountStatus?.canReceivePayouts,
    accountStatus?.chargesEnabled,
    accountStatus?.hasAccount,
    accountStatus?.isOnboarded,
    navigate,
  ]);

  const handleConnectWallet = useCallback(() => {
    navigate(EPath.PRACTICE_CONFIGURATION);
  }, [navigate]);

  const emptyStateContent = useMemo(
    () => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <BadgeIcon
          sx={{
            color: "text.disabled",
            fontSize: 48,
            opacity: 0.5,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            fontWeight: 500,
          }}
        >
          No prescriptions available
        </Typography>
      </Box>
    ),
    []
  );

  return (
    <DashboardLayout
      title="Prescription Management"
      sideButton={
        <Button
          variant="contained"
          onClick={handleNewPrescriptionClick}
          className="rounded-full px-6 py-2 font-inter text-white transition-all duration-200 hover:shadow-lg"
        >
          New Prescription
        </Button>
      }
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.border.light}`,
          borderRadius: "8px",
          p: 0,
          height: "calc(100vh - 100px)",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box
            component="div"
            className="flex !w-full !py-3 !px-4"
            justifyContent="space-between"
          >
            {/* Search Input */}
            <TextField
              fullWidth
              placeholder="Search"
              value={searchValue}
              onChange={handleSearchChange}
              size="small"
              sx={{ width: "400px" }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: colors.text.secondary }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <PrescriptionFilter
              providerOptions={filterOptions?.providers || []}
              onApply={handleFilterApply}
              initialFilters={filters}
            />
          </Box>
          <CustomTable
            columns={columns}
            data={prescriptionsData?.items}
            totalCount={totalCount}
            page={(request.page ?? 1) - 1}
            isLoading={isLoading}
            rowsPerPage={request.pageSize ?? 10}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onSortChange={handleSortChange}
            sortBy={getColumnIdFromSortBy(request.sortBy)}
            sortDirection={(request.orderBy as "asc" | "desc") || undefined}
            disableClientSideSort
            loadingMessage="Loading prescription data..."
            emptyStateContent={emptyStateContent}
            paperSx={{
              height: "calc(100% - 80px)",
              borderRadius: 0,
              border: "none",
              justifyContent: "space-between",
            }}
          />
        </Box>
      </Box>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              border: `1px solid ${colors.border.light}`,
              minWidth: 160,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate(
              EPath.PRESCRIPTION_DETAIL.replace(
                ":prescriptionId",
                selectedPrescription?.id ?? ""
              )
            );
          }}
          sx={{
            color: colors.text.primary,
            "&:hover": {
              backgroundColor: colors.background.secondary,
            },
          }}
        >
          <ListItemText primary="View details" />
        </MenuItem>
        <MenuItem
          disabled={
            selectedPrescription?.status ===
              PrescriptionResponseDtoStatusEnum.Cancelled ||
            selectedPrescription?.status ===
              PrescriptionResponseDtoStatusEnum.Paid
          }
          onClick={handleCancelPrescription}
          sx={{
            color: colors.text.primary,
            "&:hover": {
              backgroundColor: colors.error[50],
            },
          }}
        >
          <ListItemText primary="Cancel" />
        </MenuItem>
      </Menu>
      <CancelPrescriptionModal
        open={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false);
          setSelectedPrescription(null);
        }}
        onConfirm={handleConfirmCancelPrescription}
        isLoading={isCancelling}
      />
      <ConnectWalletDialog
        open={isConnectWalletDialogOpen}
        onClose={() => setIsConnectWalletDialogOpen(false)}
        isPracticeOwner={userClaims?.isPracticeOwner ?? false}
        onConnectWallet={handleConnectWallet}
      />
    </DashboardLayout>
  );
};
